import { AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Inject, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { IncidentCodeService } from '@shared/services/incident-code-service';
import { ProductionTerminalService } from '@shared/services/production-terminal.service';
import { ICompletionFlow, ICompletionGroupType, ITerminal, TerminalAdministrationService } from '@shared/services/terminalAdministration.service';
import { ITerminalTemplate, TerminalTemplateAdministrationService } from '@shared/services/terminaltemplateAdministration.service';
import { IWorktask, WorktaskService } from '@shared/services/worktask.service';
import { ValidatorPatterns } from '@shared/validatorpatterns';
import { ListPrintConfigurationComponent } from '../../mes-components/list-print-configuration/list-print-configuration.component';

@Component({
	selector: 'app-edit-terminal',
	templateUrl: './edit-terminal.component.html',
	styleUrls: ['./edit-terminal.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class EditTerminalComponent implements OnInit, AfterViewInit {
	@Output() edited: EventEmitter<any> = new EventEmitter();
	@ViewChild(ListPrintConfigurationComponent)
	printConfigurationListComponent: ListPrintConfigurationComponent;

	form: FormGroup;
	loading: boolean = false;
	terminal: ITerminal;

	completionFlows: string[] = Object.keys(ICompletionFlow).filter((key) => Number.isNaN(Number(ICompletionFlow[key])));

	completionGroupTypes: string[] = Object.keys(ICompletionGroupType).filter((key) => Number.isNaN(Number(ICompletionGroupType[key])));

	httpError: string = '';

	terminalTemplates: ITerminalTemplate[] = [];
	workTasks: IWorktask[] = [];
	terminals: ITerminal[] = [];

	categories: string[];
	uncategorizedLabel: string;
	incidentCodeGroups: string[];

	constructor(
		@Inject(MAT_DIALOG_DATA) public editTerminal: ITerminal,
		public dialogRef: MatDialogRef<EditTerminalComponent>,
		private terminalService: TerminalAdministrationService,
		private terminalTemplateService: TerminalTemplateAdministrationService,
		private workTaskService: WorktaskService,
		private incidentCodeService: IncidentCodeService,
		private translateService: TranslateService,
		private productionTerminalService: ProductionTerminalService,
		private fb: FormBuilder,
	) {
		this.terminal = { ...editTerminal };
	}

	ngAfterViewInit(): void {
		this.form.addControl('printConfigurations', this.printConfigurationListComponent.form);
	}

	ngOnInit(): void {
		this.translateService.get('mes.mes-production-terminal.uncategorized-worktask-label').subscribe((label: string) => {
			this.uncategorizedLabel = label;
		});
		this.initializeForm();
		this.populateForm();
	}

	initializeForm(): void {
		this.form = this.fb.group({
			alias: [this.terminal.alias, [Validators.required, Validators.maxLength(255), Validators.pattern(ValidatorPatterns.Alias)]],
			name: [this.terminal.name, [Validators.required, Validators.maxLength(255), Validators.pattern(ValidatorPatterns.Description)]],
			completionFlow: [this.terminal.completionFlow, [Validators.required]],
			completionGroupType: [this.terminal.completionGroupType, [Validators.required]],
			stationFilter: [this.terminal.stationFilter || '1', [Validators.maxLength(2), Validators.required, Validators.pattern(ValidatorPatterns.WholeNumber)]],
			terminalTemplateId: [this.terminal.terminalTemplateId, [Validators.required, Validators.maxLength(36), Validators.pattern(ValidatorPatterns.GuidPattern)]],
			workTaskId: [this.terminal.workTaskId, [Validators.maxLength(36), Validators.pattern(ValidatorPatterns.GuidPattern)]],
			nextId: [this.terminal.nextId, [Validators.maxLength(36), Validators.pattern(ValidatorPatterns.GuidPattern)]],
			sqlQuery: [this.terminal.sqlQuery, [Validators.maxLength(200000)]],
			workTaskCategories: [this.terminal.workTaskCategories.map((item) => (item === '' ? this.uncategorizedLabel : item))],
			incidentCodeGroups: [this.terminal.incidentCodeGroups || []],
		});
		this.form.valueChanges.subscribe((value) => {
			Object.keys(value).forEach((key) => {
				if (key in this.terminal) {
					if (key === 'workTaskCategories') {
						this.terminal.workTaskCategories = value[key].map((item) => (item === this.uncategorizedLabel ? '' : item));
					} else if (key === 'incidentCodeGroups') {
						this.terminal.incidentCodeGroups = value[key];
					} else {
						this.terminal[key] = value[key];
					}
				}
			});
		});
	}

	populateForm(): void {
		this.terminalTemplateService.getTerminalTemplates().subscribe((response) => {
			this.terminalTemplates = response.result.sort((a, b) => a.alias.toLowerCase().localeCompare(b.alias.toLowerCase()));
		});

		this.workTaskService.getWorktasks().subscribe((response) => {
			this.workTasks = response.result.filter((c) => c.isActive || c.id === this.terminal.workTaskId).sort((a, b) => a.alias.toLowerCase().localeCompare(b.alias.toLowerCase()));

			this.categories = this.workTasks
				// Get the value
				.map((c) => (c.tags['categoryName'] ? c.tags['categoryName'] : ''))
				// Distinct list
				.filter((value, index, self) => self.indexOf(value) === index)
				// Remove selected from list
				.filter((item) => !this.terminal.workTaskCategories.includes(item))
				// Replace '' with uncategorizedLabel
				.map((c) => (c === '' ? this.uncategorizedLabel : c))
				.sort();
		});

		this.terminalService.getTerminals().subscribe((response) => {
			this.terminals = response.result.filter((c) => c.id !== this.terminal.id).sort((a, b) => a.alias.toLowerCase().localeCompare(b.alias.toLowerCase()));
		});

		this.incidentCodeService.getAll().subscribe((incidentCodes) => {
			const groupedCodes = this.incidentCodeService.groupIncidentCodes(incidentCodes);
			this.incidentCodeGroups = groupedCodes
				.map((g) => g.group)
				// Remove selected from list
				.filter((item) => !this.terminal.incidentCodeGroups.includes(item))
				.sort();
		});
	}

	get controls() {
		return this.form.controls;
	}

	getFirstValidationErrorName(formControlName: string) {
		return Object.keys(this.form.controls[formControlName].errors)[0];
	}

	onCancel = () => this.dialogRef.close();

	async onSave() {
		if (this.loading) return;
		this.httpError = '';
		if (this.form.valid) {
			this.loading = true;
			this.terminal.printConfigurations = this.printConfigurationListComponent.getPayload();
			this.terminalService.updateTerminal(this.terminal).subscribe({
				next: (updatedTerminal) => {
					this.productionTerminalService.applyTerminalUpdate(updatedTerminal);
					this.edited.emit();
					this.dialogRef.close();
				},
				error: (errorResponse) => {
					this.httpError = `${errorResponse.status}`;
					this.loading = false;
				},
			});
		}
	}
}
